<template>
  <div class="profile-container">
    <h1>{{ t('profile.title') }}</h1>
    <div v-if="user" class="profile-details">
      <p><strong>{{ t('profile.fields.email') }}:</strong> {{ user.email || t('common.placeholder') }}</p>
      <p><strong>{{ t('profile.fields.name') }}:</strong> {{ user.name || t('common.placeholder') }}</p>
      <p><strong>{{ t('profile.fields.appUserId') }}:</strong> {{ user.id || t('common.placeholder') }}</p>
      <p><strong>{{ t('profile.fields.casdoorUserId') }}:</strong> {{ user.casdoorId || t('common.placeholder') }}</p>

      <div v-if="casdoorProfileUrl" class="casdoor-link-section">
        <a :href="casdoorProfileUrl" target="_blank" class="casdoor-profile-link">
          {{ t('profile.casdoor.manageAccount') }}
        </a>
      </div>
      <p v-else class="casdoor-link-loading">
        {{ t('profile.casdoor.loading') }}
      </p>
    </div>
    <div v-else class="no-user-data">
      <p>{{ t('profile.empty.message') }}</p>
      <router-link to="/auth">{{ t('profile.empty.cta') }}</router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'; // Removed 'computed' as it's not used here.
import { useCasdoor } from 'casdoor-vue-sdk';
import { useI18n } from 'vue-i18n';

/**
 * @file Profile.vue
 * @description This component displays the authenticated user's profile information retrieved
 * from localStorage. It also provides a link to manage the user's account directly in Casdoor.
 * Route guards should prevent access to this page if the user is not authenticated.
 */

/**
 * Reactive ref holding the parsed user object from localStorage.
 * @type {import('vue').Ref<object|null>}
 * @property {string} email - User's email.
 * @property {string} name - User's name.
 * @property {string} id - Application-specific user ID.
 * @property {string} casdoorId - User's unique ID from Casdoor (subject/sub).
 */
const user = ref(null);

/**
 * Reactive ref holding the URL to the user's profile management page in Casdoor.
 * @type {import('vue').Ref<string>}
 */
const casdoorProfileUrl = ref('');
const { t } = useI18n();

// Attempt to get getMyProfileUrl from useCasdoor().
/**
 * Function to retrieve the Casdoor profile management URL.
 * This is determined by trying to access `getMyProfileUrl` from the `useCasdoor` composable.
 * The exact way `getMyProfileUrl` is exposed by `casdoor-vue-sdk` via `useCasdoor` can vary,
 * so this code attempts common patterns (direct method, method on an `sdk` object).
 * @type {Function|null}
 */
let getCasdoorProfileManagementUrl = null;
try {
  const casdoorInstance = useCasdoor();
  if (casdoorInstance && typeof casdoorInstance.getMyProfileUrl === 'function') {
    getCasdoorProfileManagementUrl = casdoorInstance.getMyProfileUrl;
  } else if (casdoorInstance && casdoorInstance.sdk && typeof casdoorInstance.sdk.getMyProfileUrl === 'function') {
    getCasdoorProfileManagementUrl = () => casdoorInstance.sdk.getMyProfileUrl();
  } else {
    console.warn('[ProfilePage] Casdoor SDK or getMyProfileUrl method not available as expected from useCasdoor().');
  }
} catch (e) {
  console.error('[ProfilePage] Error while trying to get useCasdoor or its methods:', e);
}

/**
 * Vue `onMounted` lifecycle hook.
 * Retrieves user data from localStorage and attempts to generate the Casdoor profile management URL.
 */
onMounted(() => {
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      const parsedUser = JSON.parse(storedUser);
      user.value = parsedUser;

      if (getCasdoorProfileManagementUrl) {
        // Typically, getMyProfileUrl() from Casdoor SDKs is context-aware or parameterless,
        // meaning it uses the currently authenticated user's session/context within Casdoor
        // or relies on configuration set during SDK initialization.
        // If it required specific account parameters, they would be passed here, e.g.:
        // const account = { owner: 'MyTradingAppOrg', name: parsedUser.name }; // Example
        // casdoorProfileUrl.value = getCasdoorProfileManagementUrl(account);
        casdoorProfileUrl.value = getCasdoorProfileManagementUrl();

        if (!casdoorProfileUrl.value) {
           console.warn("[ProfilePage] getMyProfileUrl() returned an empty or invalid URL. Check Casdoor SDK configuration and user context with Casdoor.");
           // Fallback to a commonly known Casdoor path if SDK method fails to provide a URL.
           // This requires the Casdoor server URL to be known. For this example, using a placeholder.
           const CASDOOR_SERVER_URL_FALLBACK = 'http://localhost:8000'; // Should ideally come from a shared config
           casdoorProfileUrl.value = `${CASDOOR_SERVER_URL_FALLBACK}/account`;
        }
      } else {
        // Fallback if the SDK method for getting profile URL isn't found
        const CASDOOR_SERVER_URL_FALLBACK = 'http://localhost:8000'; // Should ideally come from a shared config
        console.warn('[ProfilePage] Falling back to manually constructed Casdoor profile URL due to unavailable SDK method.');
        casdoorProfileUrl.value = `${CASDOOR_SERVER_URL_FALLBACK}/account`;
      }

    } catch (e) {
      console.error('[ProfilePage] Error parsing user data from localStorage:', e);
      user.value = null; // Clear user data if parsing fails to prevent displaying corrupted info
    }
  } else {
    console.warn('[ProfilePage] No user data found in localStorage. User might not be logged in or data was cleared.');
    // Route guards should ideally prevent this page from being accessed if not authenticated.
  }
});
</script>

<style scoped>
.profile-container {
  max-width: 600px;
  margin: 30px auto;
  padding: 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  font-family: Arial, sans-serif;
}

h1 {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
}

.profile-details p {
  font-size: 1.05em; /* Slightly reduced for more content */
  color: #555;
  line-height: 1.7;
  border-bottom: 1px solid #eee;
  padding: 10px 0;
}

.profile-details p:first-child {
  padding-top: 0;
}
.profile-details p:last-of-type { /* Target last data paragraph before link section */
  border-bottom: none;
  margin-bottom: 20px; /* Space before Casdoor link section */
}

.profile-details strong {
  color: #333;
  margin-right: 10px;
  display: inline-block;
  min-width: 180px; /* Align values */
}

.casdoor-link-section {
  margin-top: 25px;
  text-align: center;
}

.casdoor-profile-link {
  display: inline-block;
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 5px;
  transition: background-color 0.3s ease;
}

.casdoor-profile-link:hover {
  background-color: #0056b3;
}

.casdoor-link-loading, .no-user-data {
  text-align: center;
  color: #777;
  margin-top: 20px;
}

.no-user-data a {
  display: inline-block;
  margin-top: 10px;
  color: #007bff;
}
</style>
